import React, { useEffect, useMemo, useState } from 'react';
import { t } from 'ttag';
// TODO: Replace custom local SearchFields in client and commercial with shared SearchInput from @coolremote/shared.
// https://coolautomation.atlassian.net/browse/CS-1310
// TextField + makeStyles kept from MUI — the search field here relies on InputProps (custom
// endAdornment, disableUnderline, themed classes.inputRoot), a pattern duplicated identically
// across ~15 other screens using the same searchComponent prop on Header. Shared's SearchInput
// doesn't support adornments or a clear button, reworking just this one screen would diverge
// from the other screens using the same header-search pattern and lose the clear-button
// affordance, so this was left as-is rather than a piecemeal rewrite.
import { TextField, makeStyles } from '@material-ui/core';
import { MenuSearch as Search } from '../../../svgComponents';
import Header from '../../../components/Header/Header';
import ServiceNavigationBar from '../../../components/Menu/ServiceNavigationBar';
import {
  Button,
  CloseIcon,
  ConnectedIcon,
  DeleteIcon,
  DisconnectedIcon,
  FilterRequire,
  IconButton,
  InfoIcon,
  Row,
  Spinner,
  Table,
  Tooltip,
  Typography,
  UploadIcon,
  useConfirmation,
  useToast,
} from '@coolremote/shared';
import { useStoreState } from '../../../models/RootStore';
import {
  useGetSiteModbusDevices,
  useDeleteModbusDevice,
  useUpdateModbusDevice,
  useCreateModbusDevice,
  useCreateModbusUnit,
  useModbusConnectivityWs,
  useGetSiteModbusUnitsWithDevices,
  getDeviceModbusUnits,
} from '../../../hooks/useModbusDevicesApi';
import { useGetSiteModbusDeviceTemplates } from '../../../hooks/useModbusDeviceTemplatesApi';
import { ListStatePanel } from '../ListStatePanel';
import { IModbusDevice } from '../types';
import styles from './ModbusMapping.module.css';
import DeviceMapper from '../DeviceMapping/DeviceMapper';
import ModbusSiteLibraryManagementDialog from '../DeviceMapping/ModbusSiteLibraryManagementDialog';

const useStyles = makeStyles((theme: any) => ({
  inputRoot: {
    color: theme?.palette?.colors?.menuFont || '#fff',
    fontFamily: 'Roboto',
    fontSize: '13px',
    margin: 'auto',
    width: '300px',
    display: 'flex',
    flexDirection: 'row',
    height: '36px',
    borderRadius: '4px',
    padding: '0 15px',
    backgroundColor:
      theme?.palette?.colors?.mainSearchBackground || 'rgba(0, 0, 0, 0.2)',
    maxWidth: '70%',
  },
  closeIconStyle: {
    color: 'white',
    padding: '7px',
  },
}));

const lineLabel = (line: number | undefined) => {
  if (line == null) return '—';
  return line === 0 ? 'IP / TCP' : `L${line}`;
};

const ModbusMapping: React.FC<any> = (props: any) => {
  const classes = useStyles();
  const confirm = useConfirmation();
  const { flash, flashError } = useToast();
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [editDeviceId, setEditDeviceId] = useState<string | undefined>(
    undefined
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const siteId =
    useStoreState((s) => s.selections.selections.siteId) || undefined;
  const modbusLineId = useStoreState(
    (s) => s.selections.selections.modbusLineId
  );
  const modbusDeviceId = useStoreState(
    (s) => s.selections.selections.modbusDeviceId
  );
  const allDevices = useStoreState((s) => s.devices.allDevices);

  const hasLine = modbusLineId !== null && modbusLineId !== '';
  const hasDevice = !!modbusDeviceId;
  const selectedLine =
    modbusLineId !== null && modbusLineId !== '' ? Number(modbusLineId) : null;

  const {
    data: devicesData,
    isLoading,
    refetch,
  } = useGetSiteModbusDevices(siteId) as unknown as {
    data: IModbusDevice[] | null;
    isLoading: boolean;
    refetch: () => void;
  };
  const [devices, setDevices] = useState<IModbusDevice[]>([]);
  useEffect(() => {
    if (Array.isArray(devicesData)) setDevices(devicesData);
  }, [devicesData]);

  // Live-refresh the list on gateway-driven connect/disconnect/add events,
  // instead of relying solely on the bind-triggered poll below.
  useModbusConnectivityWs(siteId, refetch);

  const { data: unitsWithDevicesData } = useGetSiteModbusUnitsWithDevices(
    siteId
  ) as unknown as { data: Record<string, any> | null };

  const { data: deviceTemplatesData } = useGetSiteModbusDeviceTemplates(
    siteId
  ) as unknown as { data: any[] | null };

  // Map from template ID → unit count.
  const templateUnitsCount = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    (deviceTemplatesData ?? []).forEach((tpl: any) => {
      if (tpl.id)
        map.set(tpl.id, Array.isArray(tpl.units) ? tpl.units.length : 0);
    });
    return map;
  }, [deviceTemplatesData]);

  // Index device templates by template ID
  const deviceTemplatesMap = useMemo<Record<string, any>>(() => {
    const map: Record<string, any> = {};
    (deviceTemplatesData ?? []).forEach((tpl: any) => {
      if (tpl.id) {
        map[tpl.id] = tpl;
      }
    });
    return map;
  }, [deviceTemplatesData]);

  // Group units by their parent modbus device
  const deviceUnitsMap = useMemo(() => {
    if (!unitsWithDevicesData) {
      return {};
    }
    const map: Record<string, any[]> = {};
    Object.values(unitsWithDevicesData).forEach((u: any) => {
      const devId = u.modbusDevice || u.device;
      if (!devId) {
        return;
      }
      if (!map[devId]) {
        map[devId] = [];
      }
      map[devId].push(u);
    });
    return map;
  }, [unitsWithDevicesData]);

  // Set of template IDs that contain at least one universal unit (entityTemplate: true).
  const universalTemplateIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    (deviceTemplatesData ?? []).forEach((tpl: any) => {
      if (
        tpl.id &&
        Array.isArray(tpl.units) &&
        tpl.units.some((u: any) => u.entityTemplate)
      ) {
        ids.add(tpl.id);
      }
    });
    return ids;
  }, [deviceTemplatesData]);

  // Devices filtered by the selected CA device/line only, before the search term is
  // applied. Used to tell "nothing to show at all" (guidance panel) apart from
  // "search term matched nothing" (inline No Results Found inside the table).
  // Base gate: only show rows whose parent CA device supports Modbus — same
  // `supportsModbus` flag the header's Modbus Device filter uses.
  const devicesBeforeSearch = useMemo(() => {
    let list = devices.filter((d) => allDevices?.[d.device || '']?.supportsModbus);
    if (modbusDeviceId) list = list.filter((d) => d.device === modbusDeviceId);
    if (selectedLine !== null)
      list = list.filter((d) => d.line === selectedLine);
    return list;
  }, [devices, modbusDeviceId, selectedLine, allDevices]);

  // FE filtering: adds the search term on top of devicesBeforeSearch
  // (matches Device Names, App Unit Names, Control UI Names, Parameter names).
  const filteredDevices = useMemo(() => {
    const list = devicesBeforeSearch;
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return list;
    }

    return list.filter((d) => {
      // 1. Device Name
      if (d.name && d.name.toLowerCase().includes(term)) return true;

      // 2. App Unit Names (matching the App Units column data: Object.values(d.units ?? {}))
      const unitNames = Object.values(d.units ?? {});
      if (
        unitNames.some(
          (name) =>
            typeof name === 'string' && name.toLowerCase().includes(term)
        )
      ) {
        return true;
      }

      // Units belonging to this device
      const units = (d.id ? deviceUnitsMap[d.id] : null) || [];
      for (const u of units) {
        // App Unit Name
        if (
          u.name &&
          typeof u.name === 'string' &&
          u.name.toLowerCase().includes(term)
        )
          return true;
        if (
          u.title &&
          typeof u.title === 'string' &&
          u.title.toLowerCase().includes(term)
        )
          return true;

        // Control UI Name
        const uiTemplateName =
          u.uiTemplate?.name ||
          (typeof u.uiTemplate === 'string' ? u.uiTemplate : null) ||
          u.paramTemplateData?.uiTemplate?.name;
        if (
          uiTemplateName &&
          typeof uiTemplateName === 'string' &&
          uiTemplateName.toLowerCase().includes(term)
        ) {
          return true;
        }

        // Parameter Names
        const rows = u.rows || u.paramTemplateData?.rows || [];
        if (Array.isArray(rows)) {
          for (const row of rows) {
            if (
              row?.name &&
              typeof row.name === 'string' &&
              row.name.toLowerCase().includes(term)
            ) {
              return true;
            }
          }
        }
      }

      // Template units if device is linked to a template
      if (d.deviceTemplate && deviceTemplatesMap[d.deviceTemplate]) {
        const tpl = deviceTemplatesMap[d.deviceTemplate];
        if (Array.isArray(tpl.units)) {
          for (const u of tpl.units) {
            // App Unit Name
            if (
              u.name &&
              typeof u.name === 'string' &&
              u.name.toLowerCase().includes(term)
            )
              return true;

            // Control UI Name
            const uiTemplateName =
              u.uiTemplate?.name ||
              (typeof u.uiTemplate === 'string' ? u.uiTemplate : null);
            if (
              uiTemplateName &&
              typeof uiTemplateName === 'string' &&
              uiTemplateName.toLowerCase().includes(term)
            ) {
              return true;
            }

            // Parameter Names
            if (Array.isArray(u.rows)) {
              for (const row of u.rows) {
                if (
                  row?.name &&
                  typeof row.name === 'string' &&
                  row.name.toLowerCase().includes(term)
                ) {
                  return true;
                }
              }
            }
          }
        }
      }

      return false;
    });
  }, [devicesBeforeSearch, searchTerm, deviceUnitsMap, deviceTemplatesMap]);

  const { mutate: deleteDevice } = useDeleteModbusDevice({
    onError: flashError,
  });
  const { mutate: updateDevice } = useUpdateModbusDevice({
    onError: flashError,
  });
  const { mutate: createDevice } = useCreateModbusDevice({
    onError: flashError,
  });
  const { mutate: createUnit } = useCreateModbusUnit({
    onError: flashError,
  });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bindingId, setBindingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const handleDuplicate = async (row: IModbusDevice) => {
    if (!row.id || !siteId) {
      return;
    }
    setDuplicatingId(row.id);
    // Read the source device's units up front so we can recreate them under the
    // copy (a duplicate must include functional units, register mappings and
    // control UI, not just the device config).
    const sourceUnits = await getDeviceModbusUnits(row.id, siteId).catch(() => []);

    const created = await createDevice({
      name: `${row.name}_duplicate`,
      vendorName: row.vendorName,
      modelName: row.modelName,
      deviceTemplate: row.deviceTemplate,
      interface: row.interface,
      line: row.line,
      slaveId: row.slaveId,
      ...(row.host != null ? { host: row.host } : {}),
      ...(row.port != null ? { port: row.port } : {}),
      timeoutMs: row.timeoutMs,
      device: row.device,
      isEngaged: false,
    });
    if (!created?.id) {
      setDuplicatingId(null);
      return;
    }

    // Recreate each unit under the new device. paramTemplateData carries the
    // register rows, universal-point mapping and control UI (uiTemplate).
    for (const u of sourceUnits) {
      await createUnit({
        modbusDeviceId: created.id,
        data: {
          name: u.name,
          pollingIntervalMs: u.pollingIntervalMs ?? 1000,
          ...(u.functionalTemplate ? { functionalTemplate: u.functionalTemplate } : {}),
          paramTemplateData: u.paramTemplateData ?? { rows: [] },
        },
      });
    }

    setDuplicatingId(null);
    refetch();
  };

  const handleBind = async (device: IModbusDevice) => {
    if (!device.id) {
      return;
    }
    setBindingId(device.id);
    const updated = await updateDevice({
      modbusDeviceId: device.id,
      data: { isEngaged: true },
    });
    setBindingId(null);
    if (!updated) {
      return;
    }
    refetch();
  };

  const handleUnbind = async (device: IModbusDevice) => {
    if (!device.id) {
      return;
    }

    const ok = await confirm({
      title: t`Unbind Device`,
      bodyMessage: t`Are you sure you want to unbind "${device.name}"?`,
      confirmText: t`Yes`,
      cancelText: t`No`,
    });

    if (!ok) {
      return;
    }

    setBindingId(device.id);
    const updated = await updateDevice({
      modbusDeviceId: device.id,
      data: { isEngaged: false },
    });
    setBindingId(null);

    if (!updated) {
      return;
    }

    refetch();
  };

  const openExistingDevice = (id: string) => {
    setEditDeviceId(id);
    setDeviceDialogOpen(true);
  };

  const openNewDevice = () => {
    if (!hasDevice) {
      flash(t`Select a device in the header before adding a Modbus device.`);
      return;
    }
    if (!hasLine) {
      flash(t`Select a line in the header before adding a device.`);
      return;
    }
    setEditDeviceId(undefined);
    setDeviceDialogOpen(true);
  };

  const handleDeviceDialogDone = () => {
    setDeviceDialogOpen(false);
    setEditDeviceId(undefined);
    refetch();
  };

  const isBound = (row: IModbusDevice) => !!row.isEngaged;
  // Device-level disconnect, mirroring BACnet (BacnetDetection): the row is
  // "disconnected" when the CA gateway it hangs off is offline OR the modbus
  // device itself is. Drives both the name-column icon and the red row tint.
  const isDisconnected = (row: IModbusDevice) =>
    !allDevices?.[row.device as string]?.isConnected || !row.isConnected;

  const searchComponent = (
    <TextField
      placeholder={t`Search...`}
      value={searchTerm}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setSearchTerm(event.target.value)
      }
      InputProps={{
        disableUnderline: true,
        classes: { root: classes.inputRoot },
        endAdornment: !searchTerm ? (
          <Search />
        ) : (
          <IconButton
            onClick={() => setSearchTerm('')}
            className={classes.closeIconStyle}
          >
            <CloseIcon />
          </IconButton>
        ),
      }}
    />
  );

  return (
    <div className={styles.view}>
      <ServiceNavigationBar {...props} />
      <div className={styles.contentArea}>
        <Header
          path={[t`Modbus Mapping`]}
          hideSystemSelection
          hideUnitSelection
          showModbusDevicesMenu
          showModbusLinesMenu
          screenTitle="Modbus Mapping"
          searchComponent={searchComponent}
        />
        {!siteId ? (
          <FilterRequire type={t`site`} expand />
        ) : (
          <div className={styles.container}>
            <div className={styles.innerContainer}>
              <div className={styles.listHead}>
                <Typography variant="pageTitle">{t`Mapped Modbus Devices`}</Typography>
                <Row gap={8}>
                  <Button
                    white
                    onClick={() => setLibraryDialogOpen(true)}
                  >
                    {t`Site Library Management`}
                  </Button>
                  <Tooltip
                    title={
                      !hasLine
                        ? t`Select a line in the header to enable this`
                        : ''
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        width={210}
                        onClick={openNewDevice}
                        disabled={!hasDevice || !hasLine}
                      >
                        + {t`New Modbus Device`}
                      </Button>
                    </span>
                  </Tooltip>
                </Row>
              </div>

              <ListStatePanel
                loading={
                  (isLoading || devicesData == null) && devices.length === 0
                }
                empty={devicesBeforeSearch.length === 0}
                emptyTitle={
                  <>
                    {t`Select a CoolAutomation Device and Line to view Modbus Mapping`}
                    <br />
                    {t`or to Add a New Modbus Device`}
                  </>
                }
                emptyIcon={
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: '#EDEBED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <UploadIcon width={50} height={40} color="#AAA2AA" />
                  </div>
                }
              >
                <div className={styles.tableScroll}>
                  <Table
                    className={styles.devicesTable}
                    stickyHeader
                    data={filteredDevices}
                    emptyMessage={
                      <div className={styles.tableEmptyState}>
                        <InfoIcon width={50} height={50} />
                        <Typography variant="cardTitle">{t`No Results Found`}</Typography>
                      </div>
                    }
                    getRowKey={(row: IModbusDevice, index: number) =>
                      row.id ?? index
                    }
                    getRowClassName={(row: IModbusDevice) =>
                      isBound(row) && isDisconnected(row) ? styles.disconnectedRow : undefined
                    }
                    columns={[
                      {
                        id: 'name',
                        title: t`Device Name`,
                        width: '22%',
                        strong: true,
                        sticky: true,
                        render: (row: IModbusDevice) => (
                          <Row gap={6} justify="space-between">
                            <span
                              className={styles.deviceName}
                              title={row.name}
                            >
                              {row.name}
                            </span>
                            {!isBound(row) ? (
                              <span className={styles.statusDash}>–</span>
                            ) : isDisconnected(row) ? (
                              <DisconnectedIcon width={18} height={18} />
                            ) : (
                              <ConnectedIcon width={18} height={18} />
                            )}
                          </Row>
                        ),
                      },
                      {
                        id: 'manufacturer',
                        title: t`Manufacturer`,
                        width: '15%',
                        sticky: true,
                        render: (row: IModbusDevice) =>
                          row.vendorName?.trim() || '—',
                      },
                      {
                        id: 'model',
                        title: t`Model`,
                        width: '10%',
                        render: (row: IModbusDevice) =>
                          row.modelName?.trim() || '—',
                      },
                      {
                        id: 'appUnits',
                        title: t`App Units`,
                        width: '8%',
                        render: (row: IModbusDevice) => {
                          const count = row.unitsCount ?? 0;
                          const unitNames = Object.values(row.units ?? {});
                          if (unitNames.length === 0) return count;
                          // A single app unit shows its own (truncated) name
                          // rather than "1" — more than one falls back to the count.
                          if (unitNames.length === 1) {
                            const name = unitNames[0];
                            const truncated =
                              name.length > 15 ? `${name.slice(0, 15)}...` : name;
                            return (
                              <Tooltip title={name} placement="top">
                                <span>{truncated}</span>
                              </Tooltip>
                            );
                          }
                          return (
                            <Tooltip
                              title={unitNames.map((name, i) => (
                                <React.Fragment key={i}>
                                  {i > 0 && <br />}
                                  {name}
                                </React.Fragment>
                              ))}
                              placement="top"
                            >
                              <span>{count}</span>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        id: 'universal',
                        title: t`Universal`,
                        width: '8%',
                        render: (row: IModbusDevice) =>
                          row.isUniversal
                            ? '✓'
                            : '—',
                      },
                      {
                        id: 'mappingStatus',
                        title: t`Status`,
                        width: '10%',
                        render: (row: IModbusDevice) => {
                          return isBound(row) ? t`Bound` : t`Not Bound`;
                        },
                      },
                      {
                        id: 'actions',
                        title: t`Actions`,
                        width: '27%',
                        render: (row: IModbusDevice) => (
                          <div className={styles.rowActions}>
                            <Button
                              white
                              size="small"
                              onClick={() => row.id && openExistingDevice(row.id)}
                            >
                              {t`Edit`}
                            </Button>
                            <Button
                              white
                              size="small"
                              disabled={duplicatingId === row.id}
                              onClick={() => handleDuplicate(row)}
                            >
                              {duplicatingId === row.id ? (
                                <Spinner size={14} />
                              ) : (
                                t`Duplicate`
                              )}
                            </Button>
                            {isBound(row) ? (
                              <Button
                                white
                                size="small"
                                disabled={bindingId === row.id}
                                onClick={() => handleUnbind(row)}
                              >
                                {bindingId === row.id ? (
                                  <Spinner size={14} />
                                ) : (
                                  t`Unbind`
                                )}
                              </Button>
                            ) : (
                              <Button
                                white
                                size="small"
                                disabled={bindingId === row.id}
                                onClick={() => handleBind(row)}
                              >
                                {bindingId === row.id ? (
                                  <Spinner size={14} />
                                ) : (
                                  t`Bind`
                                )}
                              </Button>
                            )}
                            <Tooltip
                              title={
                                isBound(row)
                                  ? t`Unbind the device before deleting`
                                  : t`Remove`
                              }
                              placement="top"
                            >
                              <span style={{ marginLeft: 'auto' }}>
                                <IconButton
                                  disableRipple
                                  size="small"
                                  className={styles.trashBtn}
                                  disabled={
                                    deletingId === row.id || isBound(row)
                                  }
                                  onClick={async () => {
                                    const name = row.name;
                                    const ok = await confirm({
                                      title: t`Delete Device`,
                                      bodyMessage: t`Are you sure you want to delete ${name}?`,
                                      confirmText: t`Yes`,
                                      cancelText: t`No`,
                                    });
                                    if (!ok) return;
                                    const id = row.id;
                                    if (!id) return;
                                    setDeletingId(id);
                                    const res = await deleteDevice(id);
                                    setDeletingId(null);
                                    if (!res) return;
                                    setDevices((prev) =>
                                      prev.filter((d) => d.id !== id)
                                    );
                                  }}
                                >
                                  {deletingId === row.id ? (
                                    <Spinner size={16} />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              </ListStatePanel>
            </div>
          </div>
        )}
      </div>

      <DeviceMapper
        open={deviceDialogOpen}
        deviceId={editDeviceId}
        onDone={handleDeviceDialogDone}
      />
      <ModbusSiteLibraryManagementDialog
        open={libraryDialogOpen}
        siteId={siteId}
        onClose={() => setLibraryDialogOpen(false)}
      />
    </div>
  );
};

export default ModbusMapping;
